import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Calendar, User, ArrowLeft, Share2, Clock, Tag, Facebook, Loader2, ChevronRight, List, BookOpen } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import Header from "../../components/Header";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = {
  sfaturi: { label: "Sfaturi cumpărători", color: "bg-blue-100 text-blue-700" },
  credite: { label: "Ghid credite", color: "bg-emerald-100 text-emerald-700" },
  piata: { label: "Piața imobiliară", color: "bg-amber-100 text-amber-700" },
  legislatie: { label: "Legislație", color: "bg-purple-100 text-purple-700" },
};

// Function to parse and format content
const formatArticleContent = (content) => {
  if (!content) return "";
  
  // If content already has HTML tags, return as is
  if (/<[^>]+>/.test(content)) {
    return content;
  }
  
  // Split content into paragraphs
  const paragraphs = content.split(/\n\n+/);
  let formattedContent = "";
  let sectionNumber = 0;
  
  paragraphs.forEach((para, index) => {
    let text = para.trim();
    if (!text) return;
    
    // Detect numbered headings like "1. Title" or "2. Something"
    const numberedHeadingMatch = text.match(/^(\d+)\.\s+(.+)$/);
    if (numberedHeadingMatch) {
      sectionNumber++;
      const title = numberedHeadingMatch[2];
      formattedContent += `<h2 id="section-${sectionNumber}" class="article-heading">${sectionNumber}. ${title}</h2>`;
      return;
    }
    
    // Detect simple headings (short lines that might be titles)
    if (text.length < 80 && !text.endsWith('.') && !text.endsWith('?') && !text.endsWith('!') && index > 0) {
      // Check if it looks like a heading
      const looksLikeHeading = /^[A-ZĂÂÎȘȚ]/.test(text) && !/^\d/.test(text);
      if (looksLikeHeading && paragraphs[index - 1]?.length > 100) {
        sectionNumber++;
        formattedContent += `<h2 id="section-${sectionNumber}" class="article-heading">${text}</h2>`;
        return;
      }
    }
    
    // Handle bullet points
    if (text.includes('\n- ') || text.startsWith('- ')) {
      const lines = text.split('\n');
      let listItems = "";
      let regularText = "";
      
      lines.forEach(line => {
        if (line.trim().startsWith('- ')) {
          if (regularText) {
            formattedContent += `<p>${regularText}</p>`;
            regularText = "";
          }
          listItems += `<li>${line.trim().substring(2)}</li>`;
        } else if (listItems) {
          formattedContent += `<ul class="article-list">${listItems}</ul>`;
          listItems = "";
          regularText = line;
        } else {
          regularText += (regularText ? " " : "") + line;
        }
      });
      
      if (listItems) {
        formattedContent += `<ul class="article-list">${listItems}</ul>`;
      }
      if (regularText) {
        formattedContent += `<p>${regularText}</p>`;
      }
      return;
    }
    
    // Regular paragraph
    formattedContent += `<p>${text}</p>`;
  });
  
  return formattedContent;
};

// Extract headings for Table of Contents
const extractHeadings = (content) => {
  const headings = [];
  if (!content) return headings;
  
  // Match numbered patterns like "1. Something" or "2. Title"
  const numberedMatches = content.match(/(?:^|\n)(\d+)\.\s+([^\n]+)/g);
  if (numberedMatches) {
    numberedMatches.forEach((match, index) => {
      const cleanMatch = match.trim();
      const parts = cleanMatch.match(/^(\d+)\.\s+(.+)$/);
      if (parts) {
        headings.push({
          id: `section-${index + 1}`,
          number: parts[1],
          title: parts[2]
        });
      }
    });
  }
  
  return headings;
};

const BlogArticle = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [showToc, setShowToc] = useState(true);
  const [headings, setHeadings] = useState([]);

  useEffect(() => {
    fetchArticle();
  }, [slug]);

  const fetchArticle = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/blog/articles/${slug}`);
      setArticle(response.data);
      
      // Extract headings for TOC
      const extractedHeadings = extractHeadings(response.data.content);
      setHeadings(extractedHeadings);
      
      // Fetch related articles
      if (response.data.category) {
        const relatedRes = await axios.get(`${API}/blog/articles?category=${response.data.category}&limit=3`);
        setRelatedArticles(relatedRes.data.articles?.filter(a => a.slug !== slug) || []);
      }
    } catch (error) {
      console.error("Error fetching article:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: article.title, url });
      } catch (e) {}
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copiat!");
    }
  };

  const shareOnFacebook = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=600,height=400');
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <div className="flex justify-center py-32">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-stone-800 mb-4">Articolul nu a fost găsit</h1>
          <Link to="/blog">
            <Button>Înapoi la Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  const category = CATEGORIES[article.category] || { label: article.category, color: "bg-stone-100 text-stone-700" };
  const formattedContent = formatArticleContent(article.content);

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      
      {/* Hero Section with Featured Image */}
      <div className="relative">
        {article.thumbnail ? (
          <div className="relative h-[50vh] min-h-[400px] max-h-[500px]">
            <img 
              src={article.thumbnail} 
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
              <div className="max-w-4xl mx-auto">
                <Link to="/blog" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors">
                  <ArrowLeft className="w-4 h-4" />
                  Înapoi la Blog
                </Link>
                
                <Badge className={`${category.color} mb-4`}>
                  {category.label}
                </Badge>
                
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                  {article.title}
                </h1>
                
                <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {article.author_name || "Echipa Imofit"}
                  </span>
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(article.published_at).toLocaleDateString('ro-RO', { 
                      day: 'numeric', 
                      month: 'long', 
                      year: 'numeric' 
                    })}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {article.read_time || "5"} min citire
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gradient-to-br from-emerald-900 via-emerald-800 to-stone-900 text-white py-16">
            <div className="max-w-4xl mx-auto px-4">
              <Link to="/blog" className="inline-flex items-center gap-2 text-emerald-200 hover:text-white mb-6 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Înapoi la Blog
              </Link>
              
              <Badge className={`${category.color} mb-4`}>
                {category.label}
              </Badge>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">{article.title}</h1>
              
              <div className="flex flex-wrap items-center gap-4 text-emerald-100 text-sm">
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {article.author_name || "Echipa Imofit"}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {new Date(article.published_at).toLocaleDateString('ro-RO', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {article.read_time || "5"} min citire
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="flex flex-col lg:flex-row gap-10">
          
          {/* Sidebar - Table of Contents */}
          {headings.length > 0 && (
            <aside className="lg:w-72 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <div className="bg-white rounded-xl border border-stone-200 p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-stone-800 flex items-center gap-2">
                      <List className="w-4 h-4 text-emerald-600" />
                      Cuprins
                    </h3>
                    <button 
                      onClick={() => setShowToc(!showToc)}
                      className="text-stone-400 hover:text-stone-600"
                    >
                      {showToc ? "Ascunde" : "Arată"}
                    </button>
                  </div>
                  
                  {showToc && (
                    <nav className="space-y-1">
                      {headings.map((heading, index) => (
                        <button
                          key={index}
                          onClick={() => scrollToSection(heading.id)}
                          className="w-full text-left px-3 py-2 text-sm text-stone-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors flex items-center gap-2"
                        >
                          <span className="w-6 h-6 bg-emerald-100 text-emerald-700 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0">
                            {heading.number}
                          </span>
                          <span className="line-clamp-2">{heading.title}</span>
                        </button>
                      ))}
                    </nav>
                  )}
                </div>
                
                {/* Share Buttons Sidebar */}
                <div className="bg-white rounded-xl border border-stone-200 p-5 shadow-sm mt-4">
                  <h3 className="font-semibold text-stone-800 mb-3 text-sm">Distribuie articolul</h3>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={shareOnFacebook} className="flex-1">
                      <Facebook className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleShare} className="flex-1">
                      <Share2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </aside>
          )}
          
          {/* Main Article Content */}
          <main className={`flex-1 ${headings.length === 0 ? 'max-w-3xl mx-auto' : ''}`}>
            {/* Excerpt / Introduction */}
            {article.excerpt && (
              <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-xl mb-8">
                <p className="text-emerald-800 text-lg leading-relaxed italic">
                  {article.excerpt}
                </p>
              </div>
            )}
            
            {/* Article Content */}
            <article className="bg-white rounded-xl border border-stone-200 p-6 sm:p-10 shadow-sm">
              <div 
                className="article-content"
                dangerouslySetInnerHTML={{ __html: formattedContent }}
              />
            </article>
            
            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className="mt-6 bg-white rounded-xl border border-stone-200 p-5 shadow-sm">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-stone-500 text-sm font-medium flex items-center gap-1">
                    <Tag className="w-4 h-4" />
                    Etichete:
                  </span>
                  {article.tags.map(tag => (
                    <Badge key={tag} variant="outline" className="text-stone-600 bg-stone-50 hover:bg-emerald-50 hover:text-emerald-700 cursor-pointer transition-colors">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            
            {/* Author Box */}
            <div className="mt-6 bg-gradient-to-br from-stone-50 to-stone-100 rounded-xl border border-stone-200 p-6 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-emerald-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {(article.author_name || "I")[0]}
                </div>
                <div>
                  <p className="font-semibold text-stone-800">{article.author_name || "Echipa Imofit"}</p>
                  <p className="text-stone-500 text-sm">Specialist imobiliar</p>
                </div>
              </div>
              <p className="mt-4 text-stone-600 text-sm">
                Articole scrise de echipa Imofit pentru a te ajuta să iei cele mai bune decizii în procesul de achiziție imobiliară.
              </p>
            </div>
            
            {/* Mobile Share Buttons */}
            {headings.length === 0 && (
              <div className="mt-6 lg:hidden bg-white rounded-xl border border-stone-200 p-5 shadow-sm">
                <div className="flex items-center gap-4">
                  <span className="text-stone-500 text-sm">Distribuie:</span>
                  <Button variant="outline" size="sm" onClick={shareOnFacebook}>
                    <Facebook className="w-4 h-4 mr-2" />
                    Facebook
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleShare}>
                    <Share2 className="w-4 h-4 mr-2" />
                    Copiază link
                  </Button>
                </div>
              </div>
            )}
          </main>
        </div>
      </div>
      
      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <div className="bg-white border-t border-stone-200 py-14">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-center gap-2 mb-8">
              <BookOpen className="w-6 h-6 text-emerald-600" />
              <h2 className="text-2xl font-bold text-stone-800">Citește și...</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedArticles.map(related => (
                <Link 
                  key={related.id}
                  to={`/blog/${related.slug}`}
                  className="group bg-stone-50 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300"
                >
                  {related.thumbnail && (
                    <div className="h-40 overflow-hidden">
                      <img 
                        src={related.thumbnail}
                        alt={related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-5">
                    <Badge className={`${CATEGORIES[related.category]?.color || "bg-stone-100 text-stone-700"} mb-2`}>
                      {CATEGORIES[related.category]?.label || related.category}
                    </Badge>
                    <h3 className="font-semibold text-stone-800 mb-2 line-clamp-2 group-hover:text-emerald-700 transition-colors">
                      {related.title}
                    </h3>
                    <p className="text-stone-500 text-sm line-clamp-2">{related.excerpt}</p>
                    <div className="mt-3 flex items-center text-emerald-600 text-sm font-medium">
                      Citește mai mult
                      <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Article Styles */}
      <style>{`
        .article-content {
          font-size: 1.125rem;
          line-height: 1.8;
          color: #44403c;
        }
        
        .article-content p {
          margin-bottom: 1.5rem;
        }
        
        .article-content h2.article-heading {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1c1917;
          margin-top: 2.5rem;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #10b981;
          scroll-margin-top: 100px;
        }
        
        .article-content h2.article-heading:first-child {
          margin-top: 0;
        }
        
        .article-content ul.article-list {
          margin: 1.5rem 0;
          padding-left: 0;
          list-style: none;
        }
        
        .article-content ul.article-list li {
          position: relative;
          padding-left: 1.75rem;
          margin-bottom: 0.75rem;
          line-height: 1.6;
        }
        
        .article-content ul.article-list li::before {
          content: "";
          position: absolute;
          left: 0;
          top: 0.6rem;
          width: 8px;
          height: 8px;
          background-color: #10b981;
          border-radius: 50%;
        }
        
        .article-content strong {
          color: #1c1917;
          font-weight: 600;
        }
        
        .article-content a {
          color: #059669;
          text-decoration: underline;
          text-underline-offset: 2px;
        }
        
        .article-content a:hover {
          color: #047857;
        }
        
        .article-content blockquote {
          border-left: 4px solid #10b981;
          padding-left: 1.5rem;
          margin: 2rem 0;
          font-style: italic;
          color: #78716c;
          background: #f5f5f4;
          padding: 1.5rem;
          border-radius: 0 0.5rem 0.5rem 0;
        }
        
        /* Style for existing HTML h2 tags */
        .article-content h2 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1c1917;
          margin-top: 2.5rem;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #10b981;
          scroll-margin-top: 100px;
        }
        
        .article-content h2:first-child {
          margin-top: 0;
        }
        
        .article-content h3 {
          font-size: 1.25rem;
          font-weight: 600;
          color: #292524;
          margin-top: 2rem;
          margin-bottom: 0.75rem;
        }
        
        /* Lists in HTML content */
        .article-content ul, .article-content ol {
          margin: 1.5rem 0;
          padding-left: 1.5rem;
        }
        
        .article-content ul li, .article-content ol li {
          margin-bottom: 0.5rem;
          line-height: 1.7;
        }
        
        .article-content ul li::marker {
          color: #10b981;
        }
        
        .article-content ol li::marker {
          color: #10b981;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default BlogArticle;
